'use client';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useCurrentUserProfile } from '../../hook/useCurrentUserProfile';
import { readFlowMeetLink, withMeetLink } from '../utils/meetLink';

// ─── Icons ─────────────────────────────────────────────────────
const RefreshIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="1 4 1 10 7 10" />
    <polyline points="23 20 23 14 17 14" />
    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
  </svg>
);
const SendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);
const CheckDoubleIcon = () => (
  <svg
    width="14"
    height="9"
    viewBox="0 0 20 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="1 6 5 10 13 2" />
    <polyline points="7 6 11 10 19 2" />
  </svg>
);
const BotIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v4" />
    <line x1="8" y1="16" x2="8" y2="16" />
    <line x1="16" y1="16" x2="16" y2="16" />
  </svg>
);

// ─── Typing Indicator ──────────────────────────────────────────
const TypingIndicator = () => (
  <div className="flex items-end gap-1.5 mb-2 text-left">
    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 text-white">
      <BotIcon />
    </div>
    <div className="bg-white rounded-xl rounded-bl-none px-3 py-2 shadow-sm border border-[#E5E5E5]">
      <div className="flex items-center gap-1">
        <span className="w-1.5 h-1.5 bg-[#8696A0] rounded-full animate-bounce [animation-delay:-0.3s]" />
        <span className="w-1.5 h-1.5 bg-[#8696A0] rounded-full animate-bounce [animation-delay:-0.15s]" />
        <span className="w-1.5 h-1.5 bg-[#8696A0] rounded-full animate-bounce" />
      </div>
    </div>
  </div>
);

// ─── Message Bubble ────────────────────────────────────────────
const MessageBubble = ({ message, isUser, timestamp, mediaType, mediaUrl }) => (
  <div
    className={`flex items-end gap-1.5 mb-2 text-left ${isUser ? 'flex-row-reverse' : ''}`}
  >
    {!isUser && (
      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 text-white">
        <BotIcon />
      </div>
    )}
    <div
      className={`relative max-w-[80%] px-3 py-2 shadow-sm text-xxs leading-relaxed whitespace-pre-wrap ${
        isUser
          ? 'bg-[#D9FDD3] text-[#111B21] rounded-xl rounded-br-none'
          : 'bg-white text-[#111B21] rounded-xl rounded-bl-none border border-[#E5E5E5]'
      }`}
    >
      {/* Render Media Attachments */}
      {!isUser && mediaType && mediaType !== 'text' && mediaUrl && (
        <div className="mb-1.5 rounded-md overflow-hidden border border-[#E5E5E5] bg-[#F0F2F5] max-w-full">
          {mediaType === 'image' && (
            <img
              src={mediaUrl}
              alt="Attached"
              className="max-h-[140px] w-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          {mediaType === 'video' && (
            <video
              src={mediaUrl}
              controls
              className="max-h-[140px] w-full object-cover"
            />
          )}
          {mediaType === 'document' && (
            <div className="p-2 flex items-center gap-1.5 text-tiny text-primary">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <a
                href={mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium truncate"
              >
                Download Document
              </a>
            </div>
          )}
        </div>
      )}

      <div>
        {message}
        {!isUser && mediaType === 'link' && mediaUrl && (
          <div className="mt-1.5 text-tiny font-medium">
            🔗{' '}
            <a
              href={mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline break-all"
            >
              {mediaUrl}
            </a>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 mt-1 justify-end">
        <span className="text-pico text-[#8696A0]">{timestamp}</span>
        {isUser && (
          <span className="text-[#53BDEB]">
            <CheckDoubleIcon />
          </span>
        )}
      </div>
    </div>
  </div>
);

// ─── Option Button (WhatsApp Style) ────────────────────────────
const OptionButton = ({ text, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full text-left px-3 py-2 rounded-[6px] text-xxs font-medium transition-all
 ${
   disabled
     ? 'bg-[#F0F2F5] text-[#8696A0] cursor-not-allowed'
     : 'bg-white text-primary border border-primary/30 hover:bg-primary/5 hover:border-primary shadow-sm cursor-pointer active:scale-[0.98]'
 }`}
  >
    {text}
  </button>
);

// ─── Confirmation Buttons ──────────────────────────────────────
const ConfirmButtons = ({ onConfirm, onDecline, disabled }) => (
  <div className="flex gap-2 w-full text-left">
    <button
      onClick={onDecline}
      disabled={disabled}
      className={`flex-1 px-3 py-2 rounded-[6px] text-xxs font-medium transition-all
 ${
   disabled
     ? 'bg-[#F0F2F5] text-[#8696A0] cursor-not-allowed'
     : 'bg-white text-status-error border border-status-error/30 hover:bg-status-error/5 hover:border-status-error shadow-sm active:scale-[0.98]'
 }`}
    >
      No ✕
    </button>
    <button
      onClick={onConfirm}
      disabled={disabled}
      className={`flex-1 px-3 py-2 rounded-[6px] text-xxs font-medium transition-all
 ${
   disabled
     ? 'bg-[#F0F2F5] text-[#8696A0] cursor-not-allowed'
     : 'bg-white text-primary border border-primary/30 hover:bg-primary/5 hover:border-primary shadow-sm active:scale-[0.98]'
 }`}
    >
      Yes ✓
    </button>
  </div>
);

// ─── Format Time ───────────────────────────────────────────────
const getTimestamp = () => {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const parseTimeToMinutes = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [hh, mm] = value.slice(0, 5).split(':').map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

const formatSlotTime = (totalMinutes) => {
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  const date = new Date();
  date.setHours(hh, mm, 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/** Preview-only slot list. Live WhatsApp options are generated by backend. */
const generateSlots = ({
  workStart,
  workEnd,
  breakStart,
  breakEnd,
  duration,
}) => {
  const start = parseTimeToMinutes(workStart);
  const end = parseTimeToMinutes(workEnd);
  const bStart = parseTimeToMinutes(breakStart);
  const bEnd = parseTimeToMinutes(breakEnd);
  const slotMins = Number(duration) || 30;
  if (start == null || end == null || slotMins <= 0) return [];

  const slots = [];
  for (let cursor = start; cursor + slotMins <= end; cursor += slotMins) {
    const slotEnd = cursor + slotMins;
    const overlapsBreak =
      bStart != null && bEnd != null && cursor < bEnd && slotEnd > bStart;
    if (!overlapsBreak) {
      slots.push({
        startMinutes: cursor,
        label: `${formatSlotTime(cursor)} – ${formatSlotTime(slotEnd)}`,
      });
    }
  }
  return slots;
};

const generatePreviewDates = (availableDaysCount) => {
  const count = Math.min(10, Math.max(1, Number(availableDaysCount) || 7));
  const dates = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 1; i <= count; i += 1) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    dates.push({
      iso: d.toISOString().slice(0, 10),
      label: d.toLocaleDateString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }),
    });
  }
  return dates;
};

// ═══════════════════════════════════════════════════════════════
// ─── FlowSimulator Component ──────────────────────────────────
// ═══════════════════════════════════════════════════════════════
export default function FlowSimulator({ flow }) {
  const { companyName, avatarUrl } = useCurrentUserProfile();
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [flowFinished, setFlowFinished] = useState(false);
  const [stepHistory, setStepHistory] = useState([]);
  const [dateTimeStage, setDateTimeStage] = useState(null); // 'date' | 'time' | null
  const [selectedAppointmentDate, setSelectedAppointmentDate] = useState(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasStartedRef = useRef(false);

  // Scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Add a bot message with typing delay
  const addBotMessage = useCallback(
    (text, callback, mediaType = null, mediaUrl = null) => {
      setIsTyping(true);
      const delay = Math.min(600 + text.length * 15, 2000);
      setTimeout(() => {
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            text,
            isUser: false,
            timestamp: getTimestamp(),
            mediaType,
            mediaUrl,
          },
        ]);
        if (callback) setTimeout(callback, 200);
      }, delay);
    },
    [],
  );

  // Add a user message
  const addUserMessage = useCallback((text) => {
    setMessages((prev) => [
      ...prev,
      { text, isUser: true, timestamp: getTimestamp() },
    ]);
  }, []);

  // Find step by order number
  const findStep = useCallback(
    (order) => {
      if (!flow?.steps) return null;
      return flow.steps.find((s) => s.order === order) || null;
    },
    [flow],
  );

  // Present a step to the user
  const presentStep = useCallback(
    (step) => {
      if (!step) {
        addBotMessage('🏁 Flow has ended. Thank you for testing!', () => {
          setFlowFinished(true);
        });
        return;
      }

      setCurrentStep(step);
      setStepHistory((prev) => [...prev, step.order]);
      setDateTimeStage(null);
      setSelectedAppointmentDate(null);

      const rawQuestion =
        typeof step.question_text === 'string' && step.question_text.trim()
          ? step.question_text
          : `Step ${step.order}`;
      const questionText = step.is_confirmation_step
        ? withMeetLink(rawQuestion, readFlowMeetLink(flow?.flow_state))
        : rawQuestion;
      if (step.is_windex_step) {
        addBotMessage(
          'Windex sends the reply in the live chat, then continues.',
          () => {
            const next = (flow?.steps || []).find(
              (item) => item.order === step.next_step_order,
            );
            if (next) presentStep(next);
            else {
              addBotMessage('🏁 Flow has ended. Thank you for testing!', () => {
                setFlowFinished(true);
                setCurrentStep(null);
              });
            }
          },
        );
        return;
      }

      addBotMessage(
        questionText,
        () => {
          if (step.is_datetime_step) {
            setDateTimeStage('date');
          } else if (step.is_open_ended && inputRef.current) {
            inputRef.current.focus();
          }
        },
        step.media_type,
        step.media_url,
      );
    },
    [addBotMessage, flow],
  );

  // Start the flow
  const startFlow = useCallback(() => {
    setMessages([]);
    setCurrentStep(null);
    setFlowFinished(false);
    setStepHistory([]);
    setInputValue('');
    setDateTimeStage(null);
    setSelectedAppointmentDate(null);

    if (!flow) return;

    const welcomeMsg = flow.welcome_message || `Welcome to ${flow.name}!`;
    addBotMessage(welcomeMsg, () => {
      // Find first step (order=1 or the first in sorted order)
      const sorted = [...(flow.steps || [])].sort((a, b) => a.order - b.order);
      if (sorted.length > 0) {
        presentStep(sorted[0]);
      } else {
        addBotMessage('⚠️ This flow has no steps configured.');
        setFlowFinished(true);
      }
    });
  }, [flow, addBotMessage, presentStep]);

  // Start on mount
  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    startFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle option selection
  const handleOptionClick = useCallback(
    (option) => {
      if (flowFinished) return;
      addUserMessage(
        typeof option.text === 'string' && option.text.trim()
          ? option.text
          : `Option ${option.number}`,
      );

      setTimeout(() => {
        if (option.next_step_order != null) {
          const nextStep = findStep(option.next_step_order);
          presentStep(nextStep);
        } else {
          addBotMessage('🏁 Flow has ended. Thank you for testing!', () => {
            setFlowFinished(true);
            setCurrentStep(null);
          });
        }
      }, 300);
    },
    [flowFinished, addUserMessage, findStep, presentStep, addBotMessage],
  );

  // Handle confirmation step
  const handleConfirmation = useCallback(
    (confirmed) => {
      if (flowFinished || !currentStep) return;
      addUserMessage(confirmed ? 'Yes ✓' : 'No ✕');

      setTimeout(() => {
        if (confirmed && currentStep.next_step_order != null) {
          const nextStep = findStep(currentStep.next_step_order);
          presentStep(nextStep);
        } else {
          addBotMessage('🏁 Flow has ended. Thank you for testing!', () => {
            setFlowFinished(true);
            setCurrentStep(null);
          });
        }
      }, 300);
    },
    [
      flowFinished,
      currentStep,
      addUserMessage,
      findStep,
      presentStep,
      addBotMessage,
    ],
  );

  // Handle open-ended / text input
  const handleSendMessage = useCallback(() => {
    if (!inputValue.trim() || flowFinished || !currentStep) return;

    const userText = inputValue.trim();
    setInputValue('');
    addUserMessage(userText);

    if (currentStep.is_google_sheet_step) {
      setTimeout(() => {
        addBotMessage(
          'Sheet lookup runs in the live WhatsApp chat. This test cannot read the Google Sheet.',
        );
      }, 300);
      return;
    }

    setTimeout(() => {
      if (currentStep.next_step_order != null) {
        const nextStep = findStep(currentStep.next_step_order);
        presentStep(nextStep);
      } else {
        addBotMessage('🏁 Flow has ended. Thank you for testing!', () => {
          setFlowFinished(true);
          setCurrentStep(null);
        });
      }
    }, 300);
  }, [
    inputValue,
    flowFinished,
    currentStep,
    addUserMessage,
    findStep,
    presentStep,
    addBotMessage,
  ]);

  const advanceAfterAppointment = useCallback(() => {
    setDateTimeStage(null);
    setSelectedAppointmentDate(null);
    if (currentStep?.next_step_order != null) {
      const nextStep = findStep(currentStep.next_step_order);
      presentStep(nextStep);
    } else {
      addBotMessage('🏁 Flow has ended. Thank you for testing!', () => {
        setFlowFinished(true);
        setCurrentStep(null);
      });
    }
  }, [currentStep, findStep, presentStep, addBotMessage]);

  const handleDateSelect = useCallback(
    (dateOption) => {
      if (flowFinished || !currentStep?.is_datetime_step) return;
      addUserMessage(dateOption.label);
      setSelectedAppointmentDate(dateOption);
      setTimeout(() => {
        setDateTimeStage('time');
        addBotMessage('Please select a time slot:');
      }, 300);
    },
    [flowFinished, currentStep, addUserMessage, addBotMessage],
  );

  const handleSlotSelect = useCallback(
    (slot) => {
      if (flowFinished || !currentStep?.is_datetime_step) return;
      const dateLabel = selectedAppointmentDate?.label || 'selected date';
      addUserMessage(`${dateLabel}, ${slot.label}`);
      setTimeout(() => {
        addBotMessage('Appointment selected successfully.', () => {
          advanceAfterAppointment();
        });
      }, 300);
    },
    [
      flowFinished,
      currentStep,
      selectedAppointmentDate,
      addUserMessage,
      addBotMessage,
      advanceAfterAppointment,
    ],
  );

  // Keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Determine the current interaction type
  const hasOptions =
    !currentStep?.is_datetime_step &&
    !currentStep?.is_quotation_step &&
    !currentStep?.is_google_sheet_step &&
    !currentStep?.is_windex_step &&
    currentStep?.options?.length > 0;
  const isConfirmation = currentStep?.is_confirmation_step;
  const isDateTime = !!currentStep?.is_datetime_step;
  const isOpenEnded =
    !isDateTime &&
    !isConfirmation &&
    !currentStep?.is_windex_step &&
    (currentStep?.is_open_ended ||
      currentStep?.is_quotation_step ||
      (!hasOptions && currentStep != null));
  const showInput = isOpenEnded && !flowFinished;
  const showOptions = hasOptions && !flowFinished;
  const showConfirmation = isConfirmation && !hasOptions && !flowFinished;
  const showDatePicker =
    isDateTime && dateTimeStage === 'date' && !flowFinished;
  const showSlotPicker =
    isDateTime && dateTimeStage === 'time' && !flowFinished;

  const previewDates = showDatePicker
    ? generatePreviewDates(currentStep.available_days_count)
    : [];
  const previewSlots = showSlotPicker
    ? generateSlots({
        workStart: currentStep.working_hours_start,
        workEnd: currentStep.working_hours_end,
        breakStart: currentStep.break_time_start,
        breakEnd: currentStep.break_time_end,
        duration: currentStep.slot_duration_minutes,
      })
    : [];

  return (
    <div className="w-[300px] h-[600px] rounded-[34px] border-[8px] border-phone-bezel bg-white relative shadow-2xl flex flex-col overflow-hidden select-none border-t-[10px] border-b-[10px] mx-auto">
      {/* Notch */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-4 bg-phone-bezel rounded-b-[14px] z-50 flex items-center justify-center">
        <div className="w-6 h-1 bg-[#1a1a1a] rounded-full" />
      </div>

      {/* Status Bar */}
      <div className="h-6 w-full flex items-center justify-between px-4 text-micro font-bold text-[#111B21] z-40 bg-white select-none pt-1 shrink-0">
        <span>
          {new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
        <div className="flex items-center gap-1">
          <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 24 24">
            <path d="M2 22h20V2z" />
          </svg>
          <div className="w-3.5 h-2 border border-[#111B21]/60 rounded-sm flex items-center p-0.5 relative">
            <div className="h-full w-3/4 bg-[#111B21]/80 rounded-[1px]" />
          </div>
        </div>
      </div>

      {/* WhatsApp Header */}
      <div className="bg-white px-3 py-2 flex items-center justify-between text-[#111B21] border-b border-[#E5E5E5] z-30 shrink-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="w-7 h-7 rounded-full object-cover shrink-0"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shrink-0 text-white font-bold text-tiny">
              {companyName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="text-left flex flex-col justify-center min-w-0">
            <div className="flex items-center gap-1">
              <span className="text-tiny font-bold leading-none truncate">
                {companyName}
              </span>
              <svg className="w-2.5 h-2.5 shrink-0" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill="#53bdeb" />
                <path
                  d="M9 12.5l2 2 4-4.5"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </div>
            <span className="text-pico text-[#8696A0] leading-none mt-0.5">
              {flowFinished
                ? 'Simulation complete'
                : isTyping
                  ? 'typing...'
                  : 'online'}
            </span>
          </div>
        </div>

        <button
          onClick={startFlow}
          className="w-6 h-6 rounded-full flex items-center justify-center text-primary hover:bg-primary/10 transition-colors shrink-0"
          title="Restart Simulation"
        >
          <RefreshIcon />
        </button>
      </div>

      {/* Chat Feed Area */}
      <div className="flex-grow flex flex-col gap-2 p-3 bg-[#F7F8FA] relative overflow-y-auto no-scrollbar justify-start">
        {/* Encryption banner */}
        <div className="self-stretch bg-[#FEF6D8] text-[#7A6A2E] text-nano leading-tight text-center py-1.5 px-2 rounded-md">
          Messages to this chat and calls are now secured with end-to-end
          encryption. Tap to learn more.
        </div>

        {/* Business account notice */}
        <div className="self-stretch bg-white border border-[#E5E5E5] text-[#8696A0] text-nano leading-tight text-center py-1.5 px-2 rounded-md">
          This chat is with a business account. Tap to learn more.
        </div>

        {messages.map((msg, i) => (
          <MessageBubble
            key={i}
            message={msg.text}
            isUser={msg.isUser}
            timestamp={msg.timestamp}
            mediaType={msg.mediaType}
            mediaUrl={msg.mediaUrl}
          />
        ))}

        {isTyping && <TypingIndicator />}

        {/* Interactive Options */}
        {showOptions && !isTyping && (
          <div className="ml-7 space-y-1.5 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {currentStep.options.length <= 3 ? (
              currentStep.options.map((opt) => (
                <OptionButton
                  key={opt.number}
                  text={
                    typeof opt.text === 'string' && opt.text.trim()
                      ? opt.text
                      : `Option ${opt.number}`
                  }
                  onClick={() => handleOptionClick(opt)}
                />
              ))
            ) : (
              <div className="w-full max-w-[220px] bg-white rounded-[6px] border border-[#E5E5E5] p-2.5 flex flex-col gap-1.5 shadow-sm">
                <span className="text-pico uppercase font-medium text-[#8696A0] tracking-wider">
                  Select Option Menu
                </span>
                <select
                  onChange={(e) => {
                    const selectedOpt = currentStep.options.find(
                      (o) => String(o.number) === e.target.value,
                    );
                    if (selectedOpt) handleOptionClick(selectedOpt);
                  }}
                  defaultValue=""
                  className="w-full bg-[#F0F2F5] text-primary border border-primary/30 hover:border-primary text-xxs font-medium rounded-[6px] px-2.5 py-2 outline-none focus:ring-1 focus:ring-primary/20 cursor-pointer transition-colors"
                >
                  <option value="" disabled>
                    Select Option...
                  </option>
                  {currentStep.options.map((opt) => (
                    <option key={opt.number} value={opt.number}>
                      {opt.text || `Option ${opt.number}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Confirmation Buttons */}
        {showConfirmation && !isTyping && (
          <div className="ml-7 max-w-[220px] animate-in fade-in slide-in-from-bottom-2 duration-300">
            <ConfirmButtons
              onConfirm={() => handleConfirmation(true)}
              onDecline={() => handleConfirmation(false)}
            />
          </div>
        )}

        {/* Date & Time — date stage */}
        {showDatePicker && !isTyping && (
          <div className="ml-7 space-y-1.5 animate-in fade-in slide-in-from-bottom-2 duration-300 max-w-[220px]">
            {previewDates.map((d) => (
              <OptionButton
                key={d.iso}
                text={d.label}
                onClick={() => handleDateSelect(d)}
              />
            ))}
          </div>
        )}

        {/* Date & Time — slot stage */}
        {showSlotPicker && !isTyping && (
          <div className="ml-7 space-y-1.5 animate-in fade-in slide-in-from-bottom-2 duration-300 max-w-[220px]">
            {previewSlots.length > 0 ? (
              previewSlots.map((slot) => (
                <OptionButton
                  key={slot.startMinutes}
                  text={slot.label}
                  onClick={() => handleSlotSelect(slot)}
                />
              ))
            ) : (
              <div className="bg-white rounded-[6px] border border-[#E5E5E5] px-3 py-2 text-xxs text-[#8696A0]">
                No slots available for this day (preview).
              </div>
            )}
          </div>
        )}

        {/* Flow Finished Card */}
        {flowFinished && (
          <div className="mx-auto my-2 max-w-[220px] bg-white rounded-[6px] px-4 py-3 shadow-sm border border-[#E5E5E5] text-center animate-in fade-in zoom-in-95 duration-300">
            <div className="text-2xl mb-1">🎉</div>
            <p className="text-xxs font-medium text-[#111B21] mb-0.5">
              Simulation Complete
            </p>
            <p className="text-pico text-[#8696A0] mb-2">
              {new Set(stepHistory).size} of {flow?.steps?.length || 0} steps
              visited
            </p>
            <button
              onClick={startFlow}
              className="w-full px-3 py-1.5 bg-primary hover:bg-primary-hover text-white text-xxs font-medium rounded-[6px] transition-colors"
            >
              <span className="flex items-center justify-center gap-1.5">
                <RefreshIcon /> Run Again
              </span>
            </button>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Bottom input bar */}
      <div className="bg-white p-1.5 flex items-center gap-1.5 border-t border-[#E5E5E5] z-30 shrink-0">
        <div className="flex-1 h-8 px-3 rounded-full bg-[#F0F2F5] flex items-center border border-transparent focus-within:border-primary transition-colors">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              flowFinished
                ? 'Simulation ended'
                : showInput
                  ? 'Type your reply...'
                  : hasOptions
                    ? 'Select an option above...'
                    : isConfirmation
                      ? 'Choose Yes or No...'
                      : isDateTime
                        ? dateTimeStage === 'time'
                          ? 'Select a time slot...'
                          : 'Select a date...'
                        : 'Waiting...'
            }
            disabled={!showInput}
            className="flex-1 bg-transparent text-[#111B21] text-xxs outline-none placeholder:text-[#8696A0] disabled:cursor-not-allowed"
          />
        </div>
        <button
          onClick={handleSendMessage}
          disabled={!showInput || !inputValue.trim()}
          className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all
 ${
   showInput && inputValue.trim()
     ? 'bg-primary hover:bg-primary-hover text-white shadow-sm active:scale-95'
     : 'bg-[#F0F2F5] text-[#8696A0] cursor-not-allowed'
 }`}
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
}
